// The panel on the left of the editor (AC-UX-10, D-69): a slim dock that is always there (undo, redo, add a picture,
// paste) and, next to it, a card showing what is selected or, otherwise, what is copied. The home for page functions,
// so the top bar stays uncluttered.
import { useEffect, useState } from 'react';
import type { History } from '../../engine/worker/protocol.ts';
import { CloseIcon, ImageIcon, PasteIcon, RedoIcon, SearchIcon, UndoIcon } from './icons.tsx';

/** What the card shows: text lines as rows, and a picture when there are images. */
export interface CardContent {
  lines: string[];
  images: number;
  pic?: string;
}

/** "3 lines", "1 image", "2 lines, 1 image". */
export function summary(c: CardContent): string {
  const n = (k: number, what: string) => (k ? [`${k} ${what}${k === 1 ? '' : 's'}`] : []);
  return [...n(c.lines.length, 'line'), ...n(c.images, 'image')].join(', ');
}

export function Dock(props: { busy: boolean; history: History; canUndoRedo: boolean; onStep: (type: 'undo' | 'redo') => void; canInsert: boolean; onAddImage: (file: File) => void; canPaste: boolean; onPaste: () => void; findOpen: boolean; onFind: () => void; card?: { open: boolean; toggle: () => void } }) {
  const { busy, history } = props;
  const plural = (n: number) => `${n} change${n === 1 ? '' : 's'}`;
  return (
    <nav data-testid="dock" className="dock" aria-label="Page tools">
      <button data-testid="undo" className="btn icon-btn" aria-label="Undo" data-count={history.undo} title={`Undo (Ctrl+Z): ${plural(history.undo)}`} disabled={busy || !history.undo || !props.canUndoRedo} onClick={() => props.onStep('undo')}>
        <UndoIcon />
      </button>
      <button data-testid="redo" className="btn icon-btn" aria-label="Redo" data-count={history.redo} title={`Redo (Ctrl+Y): ${plural(history.redo)}`} disabled={busy || !history.redo || !props.canUndoRedo} onClick={() => props.onStep('redo')}>
        <RedoIcon />
      </button>
      <label data-testid="insert-image" className={`btn icon-btn${busy || !props.canInsert ? ' is-disabled' : ''}`} aria-label="Add a picture" title="Add a PNG or JPEG: it goes into the copy, then Paste (Ctrl+V) puts it on the page">
        <input
          data-testid="insert-image-file"
          type="file"
          accept="image/png,image/jpeg"
          hidden
          disabled={busy || !props.canInsert}
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) props.onAddImage(f);
          }}
        />
        <ImageIcon />
      </label>
      <button data-testid="paste" className="btn icon-btn" aria-label="Paste" title="Paste what you copied onto the page in view (Ctrl+V)" disabled={!props.canPaste} onClick={props.onPaste}>
        <PasteIcon />
      </button>
      <button data-testid="find" className="btn icon-btn" aria-label="Find and replace" aria-pressed={props.findOpen} title="Find and replace (Ctrl+F)" onClick={props.onFind}>
        <SearchIcon />
      </button>
      {props.card && (
        // Opens and closes the card (closed by the user it stays closed; without room beside the pages it opens only from here).
        <button data-testid="card-toggle" className="btn icon-btn" aria-pressed={props.card.open} aria-label="Show the selection or copy" title={props.card.open ? 'Close the card' : 'Show what is selected or copied'} onClick={props.card.toggle}>
          {props.card.open ? '«' : '»'}
        </button>
      )}
    </nav>
  );
}

/**
 * The card next to the dock: the selection, or the copy when nothing is selected (or the user asked for it). When both
 * exist, a chip above the card names the other one and switches to it.
 */
export function Card(props: {
  width: number;
  onClose: () => void;
  mode: 'selected' | 'copied';
  content: CardContent;
  other?: { label: string; onShow: () => void };
  /** the rows can be edited and applied (the selected lines, D-69 step 2): one text per line, in the rows' order */
  onApply?: (texts: string[]) => void;
  /** the apply button's label ("Apply" for the selection, "Update copy" for the copy) */
  applyLabel?: string;
  busy: boolean;
  /** a question about the edit being applied (font, room), answered in the card */
  question?: React.ReactNode;
}) {
  const { content } = props;
  const original = content.lines.join('\n');
  const [draft, setDraft] = useState(original);
  useEffect(() => setDraft(original), [original]);
  const rows = draft.split('\n');
  const wrongRows = rows.length !== content.lines.length;
  const canApply = !!props.onApply && !props.busy && draft !== original && !wrongRows;
  const apply = () => canApply && props.onApply!(rows);
  return (
    <aside data-testid="card" data-mode={props.mode} className="card-wrap" style={{ width: props.width }} aria-label={props.mode === 'selected' ? 'Selected' : 'Copied'}>
      {props.other && (
        <button data-testid="card-chip" className="card-chip" title="Show it in the card" onClick={props.other.onShow}>
          {props.other.label}
        </button>
      )}
      <div className="card">
        <div className="card-head">
          <h2 data-testid="card-title">
            {props.mode === 'selected' ? 'Selected' : 'Copied'} · {summary(content)}
          </h2>
          <button data-testid="card-close" className="btn icon-btn" aria-label="Close the card" title="Close (open it again from the dock)" disabled={!!props.question} onClick={props.onClose}>
            <CloseIcon size={16} />
          </button>
        </div>
        {content.pic && <img data-testid="card-pic" className="card-pic" src={content.pic} alt="" />}
        {content.lines.length > 0 && (
          // One row per line (no wrapping), so the rows are the lines.
          <textarea
            data-testid="card-text"
            className="card-text"
            readOnly={!props.onApply || props.busy}
            wrap="off"
            rows={Math.min(20, Math.max(2, content.lines.length))}
            value={props.onApply ? draft : original}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                apply();
              }
            }}
            aria-label={props.onApply ? 'The text of the lines: edit, then Apply (Ctrl+Enter)' : 'The text of the lines'}
          />
        )}
        {props.onApply && content.lines.length > 0 && !props.question && (
          <div className="card-actions">
            {wrongRows && (
              <p data-testid="card-rows" className="card-note">
                One row per line ({content.lines.length}): add or remove lines on the page itself.
              </p>
            )}
            <button data-testid="card-apply" className="btn btn-primary" disabled={!canApply} title={props.mode === 'copied' ? 'Keep the changed text in the copy: Paste puts it in (Ctrl+Enter)' : 'Apply the changed rows to their lines (Ctrl+Enter)'} onClick={apply}>
              {props.applyLabel ?? 'Apply'}
            </button>
            {draft !== original && (
              <button data-testid="card-reset" className="btn" disabled={props.busy} title="Back to the lines' text" onClick={() => setDraft(original)}>
                Reset
              </button>
            )}
          </div>
        )}
        {props.question && (
          <div data-testid="card-question" className="card-question">
            {props.question}
          </div>
        )}
      </div>
    </aside>
  );
}
